import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationCircle,
  faFileVideo,
} from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import classnames from 'classnames';
import { Button, Img, ProgressBar } from 'components';
import { FileInfo } from 'infrastructure/uploads/UploadsContext';

/**
 * Formats file size.
 * @param size - file size
 * @returns formatted file size
 */
const fileSizeFormatter = (size: number) => {
  if (size === 0) {
    return '0 B';
  }

  const i = Math.floor(Math.log(size) / Math.log(1024));
  return `${(size / 1024 ** i).toFixed(2)} ${['B', 'kB', 'MB', 'GB', 'TB'][i]}`;
};

const getFileStatus = (file: FileInfo) => {
  switch (file.status) {
    case 'queued':
      return 'Queued';
    case 'uploading':
      return `Uploading · ${fileSizeFormatter(file.size)}`;
    case 'processing':
      return 'Processing';
    case 'uploaded':
      return file.destinationPath ? 'Completed · Open asset' : 'Completed';
    case 'failed':
      return 'Failed';
    default:
      return fileSizeFormatter(file.size);
  }
};

export interface FileUploadRowProps {
  file: FileInfo;
  onDismissFailedUpload?: (id: string) => void;
}

const FileUploadRow: React.FC<FileUploadRowProps> = ({
  file,
  onDismissFailedUpload,
}) => {
  const canDismissFailure =
    file.status === 'failed' &&
    file.canDismissFailure &&
    !!onDismissFailedUpload;
  const rowClassName = classnames('upload-preview-row', {
    'upload-preview-row--failure-dismissible': canDismissFailure,
  });
  const content = (
    <>
      <div className="file-preview">
        {file.type === 'image' ? (
          <div
            style={{
              transform: `rotate(${file.rotation}deg)`,
            }}
          >
            <Img
              alt={file.name}
              blurFill
              containerClassName="thumbnail"
              src={file.src}
            />
          </div>
        ) : (
          <FontAwesomeIcon className="placeholder-icon" icon={faFileVideo} />
        )}
      </div>
      <div className="file-info-container">
        <div className="file-info">
          <div className="file-name">{file.name}</div>
          <small className="file-size text-muted">{getFileStatus(file)}</small>
          {file.status === 'uploading' && (
            <ProgressBar
              color="info"
              value={file.bytesUploaded}
              total={file.size}
              size="xs"
              className="uploading-bar"
            />
          )}
        </div>
      </div>
      <div className="status-icon">
        {file.status === 'uploaded' && (
          <FontAwesomeIcon
            className="text-success"
            fixedWidth
            icon={faCheckCircle}
          />
        )}
        {file.status === 'failed' && (
          <FontAwesomeIcon
            className="text-danger"
            fixedWidth
            icon={faExclamationCircle}
          />
        )}
        {canDismissFailure && (
          <Button
            aria-label={`Dismiss failed upload ${file.name}`}
            buttonStyle="link"
            className="dismiss-failed-btn unstyled-button"
            color="danger"
            icon={<FontAwesomeIcon icon={faTimes} fixedWidth />}
            onClick={() => onDismissFailedUpload?.(file.id)}
            title="Dismiss failed upload"
          />
        )}
      </div>
    </>
  );

  if (file.status === 'uploaded' && file.destinationPath) {
    return (
      <a
        aria-label={`Open ${file.name}`}
        className={`${rowClassName} upload-preview-row--link`}
        href={file.destinationPath}
        rel="noopener noreferrer"
        target="_blank"
      >
        {content}
      </a>
    );
  }

  return <div className={rowClassName}>{content}</div>;
};

export default FileUploadRow;
